import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { TableForProductManagement } from "@components/Tables";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, formatDateForRequest, formatVerificationType } from "@utils/common";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DropIcon, AdjustIcon } from "@components/Icons";
import CustomSelect from "@components/UI/CustomSelect";
import useEscapeKey from "@hooks/useEscapeKey";

const AssignProjectModal = ({
  isOpen,
  onClose,
  onAssign,
  productSeq,
  initialSelectedVerifications = [],
  "data-testid": dataTestId = "assign-project-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { creatorList, fetchSearchInfo } = useSearchInfo();

  // State for table data
  const [allProjectsData, setAllProjectsData] = useState([]); // Store all data
  const [projectsData, setProjectsData] = useState([]); // Display data for current page
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectVerifications, setSelectedProjectVerifications] = useState([]);
  const [selectedVerifications, setSelectedVerifications] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [verificationPanelHeight, setVerificationPanelHeight] = useState(null);

  // Search state
  const [searchFilters, setSearchFilters] = useState({});
  const leftTableContainerRef = useRef(null);
  const [assignmentFilter, setAssignmentFilter] = useState(null); // null = all, "assigned" = assigned, "unassigned" = unassigned

  // TableForProductManagement data-testid suffixes (see TableForProductManagement.jsx)
  const TABLE_BODY_SELECTOR = '[data-testid$="-table-body"]';
  const TABLE_PAGINATION_SELECTOR = '[data-testid$="-pagination"]';

  // Advanced Search Fields Configuration
  const advancedSearchFields = [
    {
      name: "projectId",
      label: "Project ID",
      type: "text",
      placeholder: t("project.project-id"),
    },
    {
      name: "projectName",
      label: "Project Name",
      type: "text",
      placeholder: t("project.project-name"),
    },
    {
      name: "createrId",
      label: "Creator",
      type: "select",
      placeholder: t("project.creator"),
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: "createdAtFrom",
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      placeholder: t("common.create-at-to"),
      type: "date",
      minDate: "createdAtFrom", // Prevent selecting date before createdAtFrom
    },
  ];

  const { showAdvancedSearch, handleAdvancedSearchToggle, setShowAdvancedSearch } = useAdvancedSearch({
    searchFields: advancedSearchFields,
    quickSearchField: "projectNameForSearch",
    setSearchCriteria: setSearchFilters,
  });

  // Function to update display data based on current page and items per page
  const updateDisplayData = (data, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pageData = data.slice(startIndex, endIndex);
    setProjectsData(pageData);
  };

  // API call for getting projects with verification
  const getProjectsWithVerification = useMutationData(
    queryKeys.getProjectsWithVerification || "get_project_with_verification",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (response && typeof response === "object") {
          const data = response.object || [];
          const totalCount = response.objectCount || data.length;
          setAllProjectsData(data); // Store all data
          setTotalData(totalCount);
          setTotalPages(Math.ceil(totalCount / itemsPerPage));
          // Update display data for current page
          updateDisplayData(data, currentPage, itemsPerPage);
        } else {
          setAllProjectsData([]);
          setProjectsData([]);
          setTotalData(0);
          setTotalPages(0);
        }
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching projects:", error);
        showErrorToast(t("product.get-projects-error"));
        setAllProjectsData([]);
        setProjectsData([]);
        setTotalData(0);
        setTotalPages(0);
        setLoading(false);
      },
    }
  );

  // Fetch projects when modal opens
  useEffect(() => {
    if (isOpen) {
      setLoading(true);

      // Build request body with search filters
      const effectiveFilters = resolveSearchCriteria(searchFilters, {
        quickSearchField: "projectNameForSearch",
        isAdvancedPanelOpen: showAdvancedSearch,
      });
      const { projectNameForSearch, projectName, ...restFilters } = effectiveFilters;
      const bodyRequest = Object.keys(restFilters).reduce((acc, key) => {
        const value = restFilters[key];
        if (value !== "" && value !== undefined) {
          if (key === "createdAtFrom" || key === "createdAtTo") {
            acc[key] = formatDateForRequest(value);
          } else {
            acc[key] = value;
          }
        }
        return acc;
      }, {});

      if (projectName) {
        bodyRequest.projectName = projectName;
      } else if (projectNameForSearch) {
        bodyRequest.projectName = projectNameForSearch;
      }

      if (productSeq) {
        bodyRequest.productSeq = productSeq;
      }

      getProjectsWithVerification.mutate(bodyRequest);
    }
  }, [isOpen, searchFilters, showAdvancedSearch]);

  // Helper function to check if a project is assigned
  const isProjectAssigned = (project) => {
    if (!project || !project.verificationDetail || project.verificationDetail.length === 0) {
      return false;
    }
    
    // Check if any verification from this project is in the selected verifications
    return project.verificationDetail.some(verification =>
      selectedVerifications.some(selected =>
        selected.projectId === project.projectId &&
        selected.verificationVersion === verification.verificationVersion &&
        selected.verificationType === verification.verificationType
      )
    );
  };

  // Filter projects based on assignment filter
  const getFilteredProjects = () => {
    if (!assignmentFilter) {
      return allProjectsData;
    }

    return allProjectsData.filter(project => {
      const isAssigned = isProjectAssigned(project);
      if (assignmentFilter === "assigned") {
        return isAssigned;
      } else if (assignmentFilter === "unassigned") {
        return !isAssigned;
      }
      return true;
    });
  };

  // Reset to first page when assignment filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [assignmentFilter]);

  // Handle frontend filtering and pagination when data, filter, page or itemsPerPage changes
  useEffect(() => {
    if (allProjectsData.length > 0) {
      const filteredData = getFilteredProjects();
      const newTotalPages = Math.ceil(filteredData.length / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
        return;
      }
      setTotalData(filteredData.length);
      setTotalPages(newTotalPages);
      updateDisplayData(filteredData, currentPage, itemsPerPage);
    }
  }, [allProjectsData, assignmentFilter, selectedVerifications, currentPage, itemsPerPage]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedRows([]);
      setCurrentPage(1);
      setAllProjectsData([]);
      setProjectsData([]);
      setTotalData(0);
      setTotalPages(0);
      setSelectedVerifications([]);
      setSelectedRowIds([]);
      setSelectedProjectVerifications([]);
      setSearchFilters({});
      setShowAdvancedSearch(false);
      setVerificationPanelHeight(null);
      setAssignmentFilter(null);
    }
  }, [isOpen]);

  // Initialize selected verifications when modal opens
  useEffect(() => {
    if (isOpen && initialSelectedVerifications.length > 0) {
      setSelectedVerifications(initialSelectedVerifications);
    }
  }, [isOpen, initialSelectedVerifications]);

  // Fetch search info when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchSearchInfo();
    }
  }, [isOpen]);

  // Match verification panel height with project table (minus pagination)
  useEffect(() => {
    if (!isOpen) return;
    const container = leftTableContainerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    const updatePanelHeight = () => {
      const tableBodyEl = container.querySelector(TABLE_BODY_SELECTOR);
      if (!tableBodyEl) return;
      const paginationEl = container.querySelector(TABLE_PAGINATION_SELECTOR);
      const tableBodyHeight = tableBodyEl.getBoundingClientRect().height;
      const paginationHeight = paginationEl?.getBoundingClientRect().height || 0;
      const newHeight = tableBodyHeight - paginationHeight;
      setVerificationPanelHeight(newHeight > 0 ? newHeight : null);
    };

    const resizeObserver = new ResizeObserver(updatePanelHeight);
    const tableBodyEl = container.querySelector(TABLE_BODY_SELECTOR);
    const paginationEl = container.querySelector(TABLE_PAGINATION_SELECTOR);
    if (tableBodyEl) resizeObserver.observe(tableBodyEl);
    if (paginationEl) resizeObserver.observe(paginationEl);

    updatePanelHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen, projectsData, itemsPerPage, currentPage, showAdvancedSearch, loading]);

  // Search handlers
  const searchHandler = useCallback((inputData) => {
    setSearchFilters((prev) => ({
      ...prev,
      ...inputData,
    }));
    // Reset to first page when searching
    setCurrentPage(1);
  }, []);

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleProjectRowClick = (project) => {
    setSelectedProject(project || null);

    const verifications = project?.verificationDetail || [];
    if (verifications.length > 0) {
      setSelectedProjectVerifications(verifications);

      const currentProjectSelectedRowIds = verifications
        .filter((verification) =>
          selectedVerifications.some(
            (selected) =>
              selected.projectId === project.projectId &&
              selected.verificationVersion === verification.verificationVersion &&
              selected.verificationType === verification.verificationType
          )
        )
        .map((verification) => ({
          verificationVersion: verification.verificationVersion,
          verificationType: verification.verificationType
        }));

      setSelectedRowIds(currentProjectSelectedRowIds);
    } else {
      setSelectedProjectVerifications([]);
      setSelectedRowIds([]);
    }
  };

  // Sync verification panel height with project table body (minus pagination)
  useEffect(() => {
    if (!isOpen) return;
    const container = leftTableContainerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    const updatePanelHeight = () => {
      const paginationEl = container.querySelector(TABLE_PAGINATION_SELECTOR);
      const containerHeight = container.getBoundingClientRect().height;
      const paginationHeight = paginationEl?.getBoundingClientRect().height || 0;
      const newHeight = containerHeight - paginationHeight;
      setVerificationPanelHeight(newHeight > 0 ? newHeight : null);
    };

    const resizeObserver = new ResizeObserver(updatePanelHeight);
    resizeObserver.observe(container);

    const paginationEl = container.querySelector(TABLE_PAGINATION_SELECTOR);
    if (paginationEl) {
      resizeObserver.observe(paginationEl);
    }

    updatePanelHeight();

    return () => resizeObserver.disconnect();
  }, [isOpen, projectsData, itemsPerPage, currentPage, showAdvancedSearch, loading]);

  // Clear verification panel if the currently selected project no longer exists after search
  useEffect(() => {
    if (selectedProject) {
      const stillExists = allProjectsData.some((p) => p.projectId === selectedProject.projectId);
      if (!stillExists) {
        setSelectedProject(null);
        setSelectedProjectVerifications([]);
        setSelectedRowIds([]);
      }
    }
  }, [allProjectsData, selectedProject]);

  // Update selectedRowIds when selectedVerifications changes (only for current project)
  useEffect(() => {
    if (selectedProjectVerifications.length > 0) {
      // Get current project ID from the first verification
      const currentProjectId = selectedProjectVerifications[0]?.projectId;

      const currentProjectSelectedRowIds = selectedProjectVerifications
        .filter(verification =>
          selectedVerifications.some(selected =>
            selected.projectId === currentProjectId &&
            selected.verificationVersion === verification.verificationVersion &&
            selected.verificationType === verification.verificationType
          )
        )
        .map(verification => ({
          verificationVersion: verification.verificationVersion,
          verificationType: verification.verificationType
        }));

      setSelectedRowIds(currentProjectSelectedRowIds);
    }
  }, [selectedVerifications, selectedProjectVerifications]);

  const handleProjectCheckboxChange = (selectedProjectIds) => {
    // Find which projects were unchecked
    const previouslySelectedProjectIds = projectsData
      .filter(project =>
        selectedVerifications.some(verification =>
          verification.projectId === project.projectId
        )
      )
      .map(project => project.projectId);

    // Find unchecked projects
    const uncheckedProjectIds = previouslySelectedProjectIds.filter(
      projectId => !selectedProjectIds.includes(projectId)
    );

    if (uncheckedProjectIds.length > 0) {
      // Remove all verifications from unchecked projects
      const updatedSelectedVerifications = selectedVerifications.filter(
        verification => !uncheckedProjectIds.includes(verification.projectId)
      );
      setSelectedVerifications(updatedSelectedVerifications);

      // If the currently viewed project was unchecked, clear the selectedRowIds
      if (selectedProjectVerifications.length > 0) {
        const currentProjectId = selectedProjectVerifications[0]?.projectId;
        if (uncheckedProjectIds.includes(currentProjectId)) {
          setSelectedRowIds([]);
        }
      }
    }
  };

  const handleVerificationSelection = (selectedRows) => {
    // Get the current page's verification objects that are selected
    const currentPageSelectedVerifications = selectedProjectVerifications.filter(verification => {
      return selectedRows.some(selectedRow =>
        selectedRow.verificationVersion === verification.verificationVersion &&
        selectedRow.verificationType === verification.verificationType
      );
    });

    // Get the current page's verification objects that are NOT selected
    const currentPageUnselectedVerifications = selectedProjectVerifications.filter(verification => {
      return !selectedRows.some(selectedRow =>
        selectedRow.verificationVersion === verification.verificationVersion &&
        selectedRow.verificationType === verification.verificationType
      );
    });

    // Get current project info
    const currentProject = allProjectsData.find(p =>
      selectedProjectVerifications.length > 0 &&
      p.projectId === selectedProjectVerifications[0].projectId
    );

    // Start with all previously selected verifications
    let updatedSelectedVerifications = [...selectedVerifications];

    // Remove any verifications from current project that are no longer selected
    updatedSelectedVerifications = updatedSelectedVerifications.filter(selectedVerification => {
      // Keep verifications from other projects
      if (selectedVerification.projectId !== currentProject?.projectId) {
        return true;
      }
      // Keep verifications from current project that are still selected
      return currentPageSelectedVerifications.some(currentPageVerification =>
        currentPageVerification.verificationVersion === selectedVerification.verificationVersion &&
        currentPageVerification.verificationType === selectedVerification.verificationType
      );
    });

    // Add any new verifications from current page that are now selected
    const newSelectedVerifications = currentPageSelectedVerifications.filter(newVerification => {
      return !updatedSelectedVerifications.some(existingVerification =>
        existingVerification.verificationVersion === newVerification.verificationVersion &&
        existingVerification.verificationType === newVerification.verificationType &&
        existingVerification.projectId === newVerification.projectId
      );
    }).map(verification => ({
      ...verification,
      // Add project information for payload
      projectName: currentProject?.projectName || '',
      projectId: currentProject?.projectId || null,
      projectSeq: verification.projectSeq || null, // Use projectSeq from verification data
      verificationSeq: verification.verificationSeq || null, // Add verificationSeq for payload
    }));

    setSelectedVerifications([...updatedSelectedVerifications, ...newSelectedVerifications]);
  };

  // Function to compare two verification arrays
  const areVerificationsEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;

    // Sort both arrays by a unique key combination for comparison
    const sortFn = (a, b) => {
      const keyA = `${a.projectId}-${a.verificationVersion}-${a.verificationType}`;
      const keyB = `${b.projectId}-${b.verificationVersion}-${b.verificationType}`;
      return keyA.localeCompare(keyB);
    };

    const sorted1 = [...arr1].sort(sortFn);
    const sorted2 = [...arr2].sort(sortFn);

    return sorted1.every((item, index) => {
      const other = sorted2[index];
      return item.projectId === other.projectId &&
        item.verificationVersion === other.verificationVersion &&
        item.verificationType === other.verificationType;
    });
  };

  // Check if current selection is different from initial selection
  const hasChanges = !areVerificationsEqual(selectedVerifications, initialSelectedVerifications);

  const handleAssign = () => {
    onAssign(selectedVerifications);
    onClose();
  };

  const handleCancel = () => {
    setSelectedRows([]);
    setSelectedVerifications([]);
    onClose();
  };

  // Table configuration for projects
  const projectTableConfig = {
    columns: ["projectId", "projectName", "createrId", "createdAt"],
    columnConfig: {
      projectId: {
        style: {
          width: "25%",
          whiteSpace: "normal",
          overflowWrap: "initial",
        },
        title: t("project.project-id"),
        customRender: (value) => value || "-",
      },
      projectName: {
        style: {
          width: "30%",
          whiteSpace: "normal",
          overflowWrap: "initial",
        },
        title: t("project.project-name"),
        customRender: (value) => value || "-",
      },
      createrId: {
        style: {
          width: "20%",
          whiteSpace: "normal",
          overflowWrap: "initial",
        },
        title: t("project.creator"),
        customRender: (value) => value || "-",
      },
      createdAt: {
        style: {
          width: "25%",
          whiteSpace: "normal",
          overflowWrap: "initial",
        },
        title: t("common.create-at"),
        customRender: (value) => (value ? formatDateTime(value) : "-"),
      },
    },
  };

  // Table configuration for verification details
  const verificationTableConfig = {
    columns: ["verificationVersion", "realFileName", "verificationType", "verificationStatusCd"],
    columnConfig: {
      verificationVersion: {
        style: {
          width: "15%",
          whiteSpace: "normal",
          overflowWrap: "initial",
        },
        title: t("common.version"),
        customRender: (value) => value || "-",
      },
      realFileName: {
        style: {
          width: "35%",
          whiteSpace: "normal",
          overflowWrap: "initial",
        },
        title: t("common.verif-real-file-name"),
        customRender: (value) => value || "-",
      },
      verificationType: {
        style: {
          width: "20%",
          whiteSpace: "normal",
          overflowWrap: "initial",
        },
        title: t("common.verification-type"),
        customRender: (value) => formatVerificationType(value) || "-",
      },
      verificationStatusCd: {
        style: {
          width: "30%",
          whiteSpace: "normal",
          overflowWrap: "initial",
        },
        title: t("common.verif-Status"),
        customRender: (value) => {
          const statusStyles = {
            ON_ANALYSIS: colors.textStatusAnalysis,
            ANALYSIS_CHECK: colors.textStatusAnalysis,
            REQUEST: colors.textStatusRequest,
            ON_VERIFICATION: colors.textStatusVerification,
            ON_REVIEW: colors.textStatusReview,
            STANDBY_FOLLOWUP: colors.textStatusFollowup,
            FOLLOWUP_CHECK: colors.textStatusFollowup,
            DONE: colors.textStatusSuccess,
            REVERIFICATION: colors.textStatusReverification,
            ERROR: colors.textStatusAnalysis,
          };

          const statusColorDot = {
            ON_ANALYSIS: colors.bgStatusAnalysis,
            ANALYSIS_CHECK: colors.bgStatusAnalysis,
            REQUEST: colors.bgStatusRequest,
            ON_VERIFICATION: colors.bgStatusVerification,
            ON_REVIEW: colors.bgStatusReview,
            STANDBY_FOLLOWUP: colors.bgStatusFollowup,
            FOLLOWUP_CHECK: colors.bgStatusFollowup,
            DONE: colors.bgStatusSuccess,
            REVERIFICATION: colors.bgStatusReverification,
            ERROR: colors.bgStatusAnalysis,
          };

          const statusText = {
            ON_ANALYSIS: t("status.analyzing"),
            ANALYSIS_CHECK: t("status.analysisCheck"),
            REQUEST: t("status.request"),
            ON_VERIFICATION: t("status.onVerification"),
            ON_REVIEW: t("status.onReview"),
            STANDBY_FOLLOWUP: t("status.standbyFollowup"),
            FOLLOWUP_CHECK: t("status.followupCheck"),
            DONE: t("status.done"),
            REVERIFICATION: t("status.reverification"),
            ERROR: t("status.error"),
          };

          return (
            <span
              className={`inline-flex items-center py-0.5 text-sm text-[13px] leading-none font-semibold ${statusStyles[value] || colors.textPrimary}`}
            >
              <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[value] || colors.bgStatusMuted}`}></div>
              {statusText[value] || value}
            </span>
          );
        },
      },
    },
  };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start xl:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`${colors.bgModal} max-h-[83vh] w-[95vw] min-w-[1216px] max-w-[1400px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>{t("product.assign-project-modal-title")}</h2>
          </div>
        </div>

        {/* Description */}
        <div className="px-6 pb-4 flex-shrink-0">
          <p className={`text-sm ${colors.textSecondary}`} data-testid={`${dataTestId}-description`}>
            {t("product.select-projects-to-assign")}
          </p>
        </div>

        {/* Search Controls */}
        <div className="px-6 pb-4 flex-shrink-0" data-testid={fieldContainerTestId("search-controls")}>
          <div className="flex flex-row items-center space-y-0 space-x-4">
            {/* Search Input */}
            <div className="relative flex-1" data-testid={fieldContainerTestId("search")}>
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg className={`h-5 w-5 ${colors.textMuted}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={t("product.search-project-placeholder")}
                value={searchFilters.projectNameForSearch ?? ""}
                onChange={(e) => searchHandler({ projectNameForSearch: e.target.value })}
                className={`block w-full border py-2 pr-3 pl-10 h-10 ${colors.borderPrimary} rounded-md leading-5 ${colors.bgSecondary} ${colors.textPrimary} ${colors.borderHoverPrimary} ${colors.textActive} outline-none sm:text-sm`}
                data-testid={fieldInputTestId("search")}
              />
              {loading && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                  <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.textActive}`}></div>
                </div>
              )}
            </div>

            {/* Assignment Filter Dropdown */}
            <div className="w-48" data-testid={fieldContainerTestId("assignment-filter")}>
              <CustomSelect
                data-testid={fieldInputTestId("assignment-filter")}
                options={[
                  { value: "all", label: t("product.all-project") },
                  { value: "assigned", label: t("product.assigned-project") },
                  { value: "unassigned", label: t("product.unassigned-project") }
                ]}
                value={assignmentFilter 
                  ? { value: assignmentFilter, label: assignmentFilter === "assigned" ? t("product.assigned-project") : t("product.unassigned-project") }
                  : { value: "all", label: t("product.all-project") }
                }
                onChange={
                  (opt) => {
                    setAssignmentFilter(opt?.value === "all" ? null : opt?.value || null)
                    setSelectedProjectVerifications([]);
                    setSelectedRowIds([]);
                  }
                }
                isSearchable={false}
                clearable={false}
                className="w-full text-sm font-medium"
              />
            </div>

            {/* Advance Search Button */}
            <button
              type="button"
              onClick={handleAdvancedSearchToggle}
              className={`inline-flex items-center justify-center border px-4 py-2 h-10 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
              data-testid={fieldInputTestId("advanced-search-toggle")}
            >
              <AdjustIcon className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t("project.advancedSearchButton")}</span>
              <span className="sm:hidden">{t("project.advancedSearchButtonMobile")}</span>
            </button>
          </div>
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div className="px-6 pb-4 flex-shrink-0">
            <AdvancedSearch
              title={t("common.advanced-search")}
              searchFields={advancedSearchFields}
              searchHandler={searchHandler}
              withoutSearch={true}
              gridCols="md:grid-cols-2 lg:grid-cols-3"
            />
          </div>
        )}

        {/* Two Tables Section - Side by Side */}
        <div className="flex flex-1 px-6 pb-4 min-h-0 gap-4" data-testid={`${dataTestId}-content`}>
          {/* Left Table - Projects */}
          <div className="flex flex-col w-1/2 min-h-0" ref={leftTableContainerRef}>
            {projectsData.length === 0 && !loading ? (
              <div className={`flex items-center justify-center h-64 ${colors.bgSecondary} rounded-lg`}>
                <p className={`text-sm ${colors.textSecondary}`}>
                  {t("product.no-projects-available")}
                </p>
              </div>
            ) : (
              <TableForProductManagement
                dataTestId={`${dataTestId}-projects-table`}
                columns={projectTableConfig.columns}
                data={projectsData}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                totalData={totalData}
                loading={loading}
                columnConfig={projectTableConfig.columnConfig}
                maxRowsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                showPagination={true}
                shouldShowGoToPage={true}
                hasCheckbox={true}
                showCheckboxOnlyWhenSelected={true}
                selectedRows={projectsData.filter(project =>
                  selectedVerifications.some(verification =>
                    verification.projectId === project.projectId
                  )
                ).map(project => project.projectId)}
                onCheckboxChange={handleProjectCheckboxChange}
                onRowClick={handleProjectRowClick}
                rowKey="projectId"
                scrollableBody={true}
                isCheckboxDisabled={(row) => {
                  // Only disable if the project doesn't have any selected verifications
                  const hasSelected = selectedVerifications.some(verification =>
                    verification.projectId === row.projectId
                  );
                  return !hasSelected; // Disable only if not selected
                }}
              />
            )}
          </div>

          {/* Right Table - Verification List */}
          <div
            className={`flex flex-col w-1/2 min-h-0 overflow-hidden`}
            style={projectsData.length !== 0 ? { paddingBottom: '44px' } : undefined}
          >
            <div className={`flex flex-col flex-1 min-h-0 overflow-hidden rounded-xl border p-4 ${colors.bgSecondary} ${colors.borderPrimary}`}>
              <div className="mb-2 flex-shrink-0">
                <h3 className={`text-md font-semibold ${colors.textPrimary}`}>
                  {t("product.verification-project-version-list")}
                </h3>
              </div>
              <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                {selectedProjectVerifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center mt-12">
                    <div className="mb-2 flex justify-center">
                      <DropIcon className={`h-16 w-16 ${colors.textActive}`} />
                    </div>
                    <p className={`text-sm text-center ${colors.textSecondary}`}>
                      {selectedProject
                        ? t("product.no-done-verifications-for-project", { status: t("status.done") })
                        : t("product.select-project-to-view-verifications")}
                    </p>
                  </div>
                ) : (
                  <TableForProductManagement
                    dataTestId={`${dataTestId}-verifications-table`}
                    columns={verificationTableConfig.columns}
                    data={selectedProjectVerifications}
                    currentPage={1}
                    setCurrentPage={() => { }}
                    totalPages={1}
                    totalData={selectedProjectVerifications.length}
                    loading={false}
                    columnConfig={verificationTableConfig.columnConfig}
                    maxRowsPerPage={selectedProjectVerifications.length}
                    onItemsPerPageChange={() => { }}
                    showPagination={false}
                    shouldShowGoToPage={false}
                    hasCheckbox={true}
                    selectedRows={selectedRowIds}
                    onCheckboxChange={handleVerificationSelection}
                    rowKey={["verificationVersion", "verificationType"]}
                    scrollableBody={true}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Sticky */}
        <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0 border-t`} data-testid={`${dataTestId}-footer`}>
          <div className="flex gap-3 flex-row items-center justify-end">
            <div className="flex items-center gap-3">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleCancel}>
                {t("common.cancel-button")}
              </OutlineButton>
              <PrimaryButton
                data-testid={`${dataTestId}-confirm-button`}
                onClick={handleAssign}
                disabled={!hasChanges}
              >
                {t("common.add-button")}
                {` (${selectedVerifications.length})`}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignProjectModal;
